import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Tag, X } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useAuth, apiError } from "../context/AuthContext";
import { useStore } from "../context/StoreContext";
import { http, money } from "../lib/api";
import { useSeo } from "../lib/seo";
import { shippingFor } from "../components/shop/CartDrawer";
import { EmptyState } from "../components/shop/Common";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { user } = useAuth();
  const { settings } = useStore();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [disc, setDisc] = useState(null);
  const [busy, setBusy] = useState(false);
  useSeo({ title: "Checkout", noindex: true });

  if (!items.length)
    return (
      <div className="max-w-3xl mx-auto px-4 py-16" data-testid="checkout-page">
        <EmptyState title="Nothing to check out yet" text="Add some vacation gear to your cart first." testid="checkout-empty">
          <Link to="/destinations" className="rounded-full bg-sun px-6 py-3 font-bold text-navy">Find your destination</Link>
        </EmptyState>
      </div>
    );

  const discount = disc?.amount || 0;
  const ship = shippingFor(subtotal - discount, settings);
  const apply = async () => {
    try {
      setDisc((await http.post("/discounts/validate", { code, subtotal })).data);
      toast.success("Discount applied");
    } catch (e) {
      setDisc(null);
      toast.error(apiError(e));
    }
  };
  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await http.post("/checkout", {
        items: items.map(({ slug, color, size, qty }) => ({ slug, color, size, qty })),
        email: user ? undefined : email,
        discount_code: disc?.code,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(apiError(err));
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid="checkout-page">
      <h1 className="text-4xl sm:text-5xl font-extrabold text-navy">Checkout</h1>
      <form onSubmit={pay} className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-10 [&>*]:min-w-0">
        <div className="space-y-8">
          <div className="rounded-[28px] border border-border p-6 sm:p-8">
            <h2 className="text-xl font-bold text-navy">Contact</h2>
            {user ? (
              <p className="mt-3 text-navy/70" data-testid="checkout-signed-in">Signed in as <b>{user.email}</b></p>
            ) : (
              <>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for order updates" className="mt-4 w-full h-13 py-3.5 rounded-full border-2 border-border px-5 outline-none focus:border-turq" data-testid="checkout-email-input" />
                <p className="mt-3 text-sm text-navy/60">Checking out as a guest. <Link to="/login?next=/checkout" className="underline font-semibold" data-testid="checkout-signin-link">Sign in</Link> to save this order to your account.</p>
              </>
            )}
          </div>
          <div className="rounded-[28px] border border-border p-6 sm:p-8">
            <h2 className="text-xl font-bold text-navy">Shipping & payment</h2>
            <p className="mt-3 text-navy/70">You'll enter your U.S. shipping address and pay securely on Stripe (cards, Apple Pay, Google Pay and more where available). We never see or store your card details.</p>
            <p className="mt-3 text-sm text-navy/60">Printed on demand: production ~2–7 business days, then 3–8 business days shipping. Estimates, not guarantees.</p>
          </div>
        </div>
        <aside className="rounded-[28px] bg-sand p-6 sm:p-8 h-fit lg:sticky lg:top-28" data-testid="checkout-summary">
          <h2 className="text-xl font-bold text-navy">Order summary</h2>
          <ul className="mt-4 divide-y divide-navy/10">
            {items.map((i) => (
              <li key={i.key} className="py-3 flex gap-3 text-sm">
                <img src={i.image} alt="" className="w-14 h-14 rounded-xl object-cover" />
                <span className="flex-1 text-navy"><b>{i.name}</b><br /><span className="text-navy/60">{i.color} · {i.size} · ×{i.qty}</span></span>
                <span className="font-semibold">{money(i.price * i.qty)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4">
            {disc ? (
              <div className="flex items-center justify-between rounded-full bg-white px-4 h-12 text-sm" data-testid="applied-discount">
                <span className="flex items-center gap-2 font-semibold text-navy"><Tag className="w-4 h-4 text-tpink" /> {disc.code}</span>
                <button type="button" onClick={() => setDisc(null)} aria-label="Remove discount" data-testid="remove-discount"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Discount code" className="flex-1 min-w-0 h-12 rounded-full px-4 border border-border bg-white text-sm uppercase" data-testid="discount-code-input" />
                <button type="button" disabled={!code} onClick={apply} className="h-12 px-5 rounded-full bg-navy text-white font-semibold text-sm disabled:opacity-40" data-testid="apply-discount-button">Apply</button>
              </div>
            )}
          </div>
          <dl className="mt-6 space-y-2 text-navy text-sm">
            <div className="flex justify-between"><dt>Subtotal</dt><dd data-testid="checkout-subtotal">{money(subtotal)}</dd></div>
            {discount > 0 && <div className="flex justify-between text-tpink"><dt>Discount</dt><dd data-testid="checkout-discount">-{money(discount)}</dd></div>}
            <div className="flex justify-between"><dt>Shipping (U.S.)</dt><dd data-testid="checkout-shipping">{ship ? money(ship) : "FREE"}</dd></div>
            <div className="flex justify-between text-navy/60"><dt>Tax</dt><dd>Calculated by Stripe</dd></div>
            <div className="flex justify-between border-t border-navy/10 pt-3 text-lg font-extrabold"><dt>Total</dt><dd data-testid="checkout-total">{money(subtotal - discount + ship)}</dd></div>
          </dl>
          <button disabled={busy} className="mt-6 w-full h-14 rounded-full bg-sun text-navy font-extrabold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="pay-with-stripe-button">
            <Lock className="w-4 h-4" /> {busy ? "Redirecting to Stripe..." : "Pay securely"}
          </button>
        </aside>
      </form>
    </div>
  );
}
